import { useTheme } from '../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
const trackCtaClick = (location: string) => {
  window.dataLayer = window.dataLayer || [];
  (window.dataLayer as any[]).push({
    event: "cta_click",
    cta_type: "termin_buchen",
    cta_location: location
  });
};

import { trackPhoneConversion } from '../../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';

export default function Hero() {
  const { theme, tonality, currentLook, setCurrentLook } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Scroll direction detection
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrollingDown = currentY > lastScrollYRef.current;
      if (scrollingDown && currentY > 60) {
        setHideNav(true);
        setIsMobileMenuOpen(false);
      } else if (!scrollingDown) {
        setHideNav(false);
      }
      lastScrollYRef.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (window.location.pathname === '/' || window.location.pathname === __BASE_PATH__) {
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleRefsClick = () => {
    setIsMobileMenuOpen(false);
    const scroll = () => {
      const element = document.getElementById('transformation');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => window.dispatchEvent(new CustomEvent('openReferenzen')), 600);
      }
    };
    if (window.location.pathname === '/' || window.location.pathname === __BASE_PATH__) {
      scroll();
    } else {
      navigate('/');
      setTimeout(scroll, 150);
    }
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // Seitenschmied Look – full-screen image hero
  if (currentLook === 'seitenschmied') {
    return (
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ fontFamily: theme.fonts.body }}
      >
        {/* Full-screen background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20professional%20web%20designer%20workspace%20in%20a%20bright%20airy%20studio%20in%20Bavaria%20Germany%2C%20clean%20wooden%20desk%20with%20large%20monitor%20showing%20a%20beautiful%20website%20design%2C%20natural%20light%20streaming%20through%20large%20windows%2C%20green%20plants%20in%20background%2C%20minimalist%20Scandinavian%20interior%20with%20warm%20wood%20tones%20and%20white%20walls%2C%20laptop%20and%20design%20tools%20neatly%20arranged%2C%20calm%20productive%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20professional%20photography%20style%2C%20warm%20neutral%20color%20palette%20with%20soft%20shadows%2C%20contemporary%20creative%20workspace%20aesthetic&width=1920&height=1080&seq=hero-bg-seitenschmied-v3&orientation=landscape"
            alt="Seitenschmied Workspace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
        </div>

        {/* Navigation */}
        <nav
          className="fixed top-0 left-0 right-0 z-50"
          id="navbar"
          style={{
            backgroundColor: (hideNav && !isMobileMenuOpen) ? 'transparent' : 'rgba(255,255,255,0.95)',
            backdropFilter: (hideNav && !isMobileMenuOpen) ? 'none' : 'blur(12px)',
            boxShadow: (hideNav && !isMobileMenuOpen) ? 'none' : '0 2px 15px rgba(0,0,0,0.05)',
            transition: 'background-color 0.35s ease, backdrop-filter 0.35s ease, box-shadow 0.35s ease',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4">
            <div className="flex items-center justify-between">

              {/* Logo + Badge */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                  onClick={() => { setCurrentLook('seitenschmied'); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="cursor-pointer flex-shrink-0"
                  aria-label="Zurück zur Startseite"
                >
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-6 sm:h-7 w-auto" />
                </button>
                <span
                  className="hidden sm:block w-px h-4 bg-gray-300 flex-shrink-0 transition-opacity duration-300"
                  style={{ opacity: hideNav ? 0 : 1 }}
                />
                <span
                  className="hidden sm:inline-flex items-center gap-1.5 text-gray-400 text-[10px] tracking-widest uppercase font-medium whitespace-nowrap transition-opacity duration-300"
                  style={{ opacity: hideNav ? 0 : 1 }}
                >
                  Websites made with <span className="text-amber-400">🧠</span>
                </span>
              </div>

              {/* Desktop Nav links + Call button — grouped right */}
              <div className="hidden md:flex items-center gap-6 lg:gap-8">
                {/* Links — fade out on scroll down */}
                <div
                  className="flex items-center gap-6 lg:gap-8 transition-opacity duration-300"
                  style={{ opacity: hideNav ? 0 : 1, pointerEvents: hideNav ? 'none' : 'auto' }}
                >
                  <button
                    onClick={handleRefsClick}
                    className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ color: theme.colors.text }}
                  >
                    Referenzen
                  </button>
                  <Link to="/hotels" className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text }}>
                    Hotels
                  </Link>
                  <Link to="/website-wirkung" className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text }}>
                    Gute Gründe
                  </Link>
                  <Link to="/ueber-uns" className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text }}>
                    Über Seitenschmied
                  </Link>
                  <Link to="/faq" className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap" style={{ color: theme.colors.text }}>
                    FAQ
                  </Link>
                </div>

                {/* Call button — always visible */}
                <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <button
                    className="flex items-center gap-2 px-5 lg:px-6 py-2.5 font-semibold text-sm rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-white"
                    style={{ background: GRADIENT }}
                  >
                    <i className="ri-phone-line text-sm"></i>
                    Jetzt anrufen
                  </button>
                </a>
              </div>

              {/* Mobile Burger — gradient icon, always visible */}
              <button
                className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer flex-shrink-0"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              >
                <i
                  className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}
                  style={{
                    background: GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                ></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-3 pb-4">
                <div className="flex flex-col pt-3">
                  <button onClick={handleRefsClick} className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 cursor-pointer rounded-lg" style={{ color: theme.colors.text }}>Referenzen</button>
                  <Link to="/hotels" onClick={() => setIsMobileMenuOpen(false)} className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 block rounded-lg" style={{ color: theme.colors.text }}>Hotels</Link>
                  <Link to="/website-wirkung" onClick={() => setIsMobileMenuOpen(false)} className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 block rounded-lg" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                  <Link to="/ueber-uns" onClick={() => setIsMobileMenuOpen(false)} className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 block rounded-lg" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                  <Link to="/faq" onClick={() => setIsMobileMenuOpen(false)} className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 block rounded-lg" style={{ color: theme.colors.text }}>FAQ</Link>
                  <div className="pt-3">
                    <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); trackPhoneConversion('tel:+491607676763'); }}>
                      <button
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 font-semibold text-sm rounded-xl cursor-pointer text-white"
                        style={{ background: GRADIENT }}
                      >
                        <i className="ri-phone-line text-sm"></i>
                        Jetzt anrufen
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full pt-24 pb-16">
          <div className="max-w-2xl">

            {/* Headline */}
            <div
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.9s ease 0.25s, transform 0.9s ease 0.25s',
              }}
            >
              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-5 leading-[1.05] text-white"
                style={{ fontFamily: theme.fonts.heading }}
              >
                Online sein kann jeder.<br />
                <span style={{
                  background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Eindruck machen nicht.
                </span>
              </h1>
            </div>

            {/* Subline */}
            <div
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.9s ease 0.4s, transform 0.9s ease 0.4s',
              }}
            >
              <p className="text-base md:text-lg mb-8 leading-relaxed text-gray-200 max-w-xl">
                Seitenschmied baut Websites, die klarer kommunizieren, professioneller wirken und aus Besuchern eher Anfragen machen.
              </p>
            </div>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row gap-3"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.9s ease 0.55s, transform 0.9s ease 0.55s',
              }}
            >
              <a href="tel:+491607676763" className="w-full sm:w-auto" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <button
                  className="w-full sm:w-auto px-8 md:px-10 py-4 font-bold text-base rounded-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2 justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                    color: '#FFFFFF',
                    boxShadow: `0 10px 30px ${theme.colors.accent}4d`,
                  }}
                >
                  <i className="ri-phone-line text-base"></i>
                  Jetzt anrufen
                </button>
              </a>
              <Link to="/termin-buchen" className="w-full sm:w-auto" onClick={() => trackCtaClick("hero")}>
                <button
                  className="w-full sm:w-auto px-8 md:px-10 py-4 font-semibold text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: '#FFFFFF',
                    border: '2px solid rgba(255,255,255,0.35)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  Online-Termin vereinbaren
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Clean Look
  if (currentLook === 'clean') {
    return (
      <section className="min-h-screen relative overflow-hidden" style={{ fontFamily: theme.fonts.body }}>
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" id="navbar-clean" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setCurrentLook('seitenschmied'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Zurück zur Startseite">
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-7 w-auto" />
                </button>
                <span className="hidden sm:block w-px h-4 bg-gray-300"></span>
                <span className="hidden sm:inline-flex items-center gap-1.5 text-gray-400 text-[10px] tracking-widest uppercase font-medium whitespace-nowrap">Websites made with <span className="text-amber-400">🧠</span></span>
              </div>
              <div className="hidden md:flex items-center gap-7">
                <a href="#prozess" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text }}>Prozess</a>
                <Link to="/website-wirkung" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                <Link to="/ueber-uns" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                <Link to="/termin-buchen">
                  <button className="px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer" style={{ backgroundColor: theme.colors.accent, color: '#FFFFFF' }}>Termin buchen</button>
                </Link>
              </div>
              <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: theme.colors.text }}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12" style={{ background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.cardBg} 100%)` }}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-5"><img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/6a1f0edae4fce559ce3adc7194b5ce70.png" alt="seitenschmied.com" className="h-12 w-auto mx-auto" /></div>
            <h1 className="text-5xl font-bold mb-4 leading-tight" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>{tonality.hero.headline}</h1>
            <p className="text-lg mb-6 max-w-2xl mx-auto" style={{ color: theme.colors.textSecondary }}>{tonality.hero.subheadline}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <a href="tel:+491607676763" className="flex items-center gap-2 px-7 py-3.5 font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: theme.colors.accent }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <i className="ri-phone-line text-sm"></i>
                Jetzt anrufen
              </a>
              <Link to="/termin-buchen" className="flex items-center gap-2 px-7 py-3.5 font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: 'transparent', border: `2px solid ${theme.colors.text}`, backdropFilter: 'blur(8px)' }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                Online-Termin vereinbaren
              </Link>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{__html: `window.addEventListener('scroll',function(){var n=document.getElementById('navbar-clean');if(n){if(window.scrollY>50){n.style.backgroundColor='rgba(255,255,255,0.98)';n.style.boxShadow='0 2px 20px rgba(0,0,0,0.1)';}else{n.style.backgroundColor='rgba(255,255,255,0.95)';n.style.boxShadow='0 1px 10px rgba(0,0,0,0.05)';}}})`}} />
      </section>
    );
  }

  // Bold Look
  if (currentLook === 'bold') {
    return (
      <section className="min-h-screen relative overflow-hidden" style={{ fontFamily: theme.fonts.body, backgroundColor: '#FFFFFF' }}>
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" id="navbar-bold" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setCurrentLook('seitenschmied'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Zurück zur Startseite">
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-7 w-auto" />
                </button>
              </div>
              <div className="hidden md:flex items-center gap-7">
                <a href="#prozess" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text }}>Prozess</a>
                <Link to="/website-wirkung" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                <Link to="/ueber-uns" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                <Link to="/termin-buchen">
                  <button className="px-5 py-2.5 font-black text-sm rounded-full transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer" style={{ background: `linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.secondary} 100%)`, color: theme.colors.primary }}>Termin buchen</button>
                </Link>
              </div>
              <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: theme.colors.text }}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 w-full h-full" style={{ background: theme.colors.primary, clipPath: 'polygon(0 0, 60% 0, 45% 100%, 0 100%)' }}></div>
          <div className="absolute inset-0 w-full h-full" style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`, clipPath: 'polygon(60% 0, 100% 0, 100% 100%, 45% 100%)' }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 pt-20 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[calc(100vh-120px)]">
            <div>
              <div className="mb-5"><img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/6a1f0edae4fce559ce3adc7194b5ce70.png" alt="seitenschmied.com" className="h-12 w-auto brightness-0 invert" /></div>
              <h1 className="text-5xl lg:text-6xl font-black mb-5 leading-none text-white transform -skew-y-1" style={{ fontFamily: theme.fonts.heading, textShadow: '4px 4px 0px rgba(0,0,0,0.3)' }}>{tonality.hero.headline}</h1>
              <p className="text-lg mb-6 text-white font-bold leading-relaxed">Für Unternehmen aus der Region, die online überzeugen wollen.</p>
              <div className="flex flex-col gap-3">
                <a href="tel:+491607676763" className="flex items-center gap-2 px-8 py-4 font-black text-base transition-all duration-300 hover:translate-x-4 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: theme.colors.accent }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <span className="flex items-center gap-2"><i className="ri-phone-line text-xl"></i> Jetzt anrufen</span>
                  <i className="ri-arrow-right-line text-2xl transition-transform group-hover:translate-x-3"></i>
                </a>
                <Link to="/termin-buchen" className="flex items-center gap-2 px-8 py-4 font-black text-base transition-all duration-300 hover:translate-x-4 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: 'transparent', border: `2px solid ${theme.colors.text}`, backdropFilter: 'blur(8px)' }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <span>Online-Termin vereinbaren</span>
                  <i className="ri-arrow-right-line text-2xl transition-transform group-hover:translate-x-3"></i>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-28 h-28 border-8 border-white transform rotate-12"></div>
              <div className="absolute bottom-12 -left-6 w-24 h-24 bg-white transform -rotate-12"></div>
              <div className="relative bg-white p-8 transform rotate-2 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
                <div className="text-6xl font-black mb-2 transform -rotate-3" style={{ fontFamily: theme.fonts.heading, color: theme.colors.secondary }}>∞</div>
                <div className="text-xl font-black mb-2" style={{ color: theme.colors.primary }}>Möglichkeiten</div>
                <div className="text-base font-bold" style={{ color: theme.colors.textSecondary }}>Deine Website. Dein Look. Dein Erfolg.</div>
              </div>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{__html: `window.addEventListener('scroll',function(){var n=document.getElementById('navbar-bold');if(n){if(window.scrollY>50){n.style.backgroundColor='rgba(255,255,255,0.98)';n.style.boxShadow='0 2px 20px rgba(0,0,0,0.1)';}else{n.style.backgroundColor='rgba(255,255,255,0.95)';n.style.boxShadow='0 1px 10px rgba(0,0,0,0.05)';}}})`}} />
      </section>
    );
  }

  // Warm Look
  if (currentLook === 'warm') {
    return (
      <section className="min-h-screen relative overflow-hidden" style={{ fontFamily: theme.fonts.body }}>
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" id="navbar-warm" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setCurrentLook('seitenschmied'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Zurück zur Startseite">
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-7 w-auto" />
                </button>
              </div>
              <div className="hidden md:flex items-center gap-7">
                <a href="#prozess" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text }}>Prozess</a>
                <Link to="/website-wirkung" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                <Link to="/ueber-uns" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                <Link to="/termin-buchen">
                  <button className="px-5 py-2.5 font-bold text-sm rounded-lg transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer" style={{ backgroundColor: theme.colors.accent, color: '#FFFFFF' }}>Termin buchen</button>
                </Link>
              </div>
              <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: theme.colors.text }}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="absolute inset-0 z-0">
          <img src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20artisan%20workshop%20interior%20with%20wooden%20workbench%20bathed%20in%20golden%20hour%20sunlight%20streaming%20through%20large%20windows%2C%20handcrafted%20tools%20neatly%20organized%20on%20rustic%20wooden%20walls%2C%20warm%20amber%20honey%20and%20caramel%20color%20tones%20throughout%2C%20cozy%20atmosphere%20with%20soft%20natural%20shadows%2C%20rich%20wooden%20textures%20and%20grain%20visible%2C%20professional%20craftsmanship%20setting%20with%20leather%20apron%20hanging%2C%20peaceful%20and%20welcoming%20ambiance%2C%20shallow%20depth%20of%20field%20focusing%20on%20crafted%20details%2C%20natural%20materials%20like%20aged%20wood%20and%20worn%20leather%20prominently%20featured%2C%20warm%20color%20palette%20dominated%20by%20browns%20beiges%20soft%20oranges%20and%20golden%20yellows%2C%20inviting%20homey%20feeling&width=1920&height=1080&seq=warm-hero-bg-002&orientation=landscape" alt="Handwerkliche Atmosphäre" className="w-full h-full object-cover" style={{ objectPosition: 'center' }} />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 pt-20 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center min-h-[calc(100vh-120px)]">
            <div>
              <div className="mb-5"><img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/6a1f0edae4fce559ce3adc7194b5ce70.png" alt="seitenschmied.com" className="h-12 w-auto" /></div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight text-white" style={{ fontFamily: theme.fonts.heading, textShadow: '3px 3px 6px rgba(0,0,0,0.5)' }}>{tonality.hero.headline}</h1>
              <p className="text-base mb-6 leading-relaxed text-white font-medium" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{tonality.hero.subheadline}</p>
              <div className="flex gap-3 flex-wrap">
                <a href="tel:+491607676763" className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: theme.colors.accent }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  <i className="ri-phone-line text-sm"></i>
                  Jetzt anrufen
                </a>
                <Link to="/termin-buchen" className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: 'transparent', border: `2px solid ${theme.colors.text}`, backdropFilter: 'blur(8px)' }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                  Online-Termin vereinbaren
                </Link>
              </div>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{__html: `window.addEventListener('scroll',function(){var n=document.getElementById('navbar-warm');if(n){if(window.scrollY>50){n.style.backgroundColor='rgba(255,255,255,0.98)';n.style.boxShadow='0 2px 20px rgba(0,0,0,0.1)';}else{n.style.backgroundColor='rgba(255,255,255,0.95)';n.style.boxShadow='0 1px 10px rgba(0,0,0,0.05)';}}})`}} />
      </section>
    );
  }

  // Premium Look
  if (currentLook === 'premium') {
    return (
      <section className="py-20 px-4 relative overflow-hidden" style={{ backgroundColor: theme.colors.background, fontFamily: theme.fonts.body }}>
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" id="navbar-premium" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setCurrentLook('seitenschmied'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Zurück zur Startseite">
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-7 w-auto" />
                </button>
              </div>
              <div className="hidden md:flex items-center gap-7">
                <a href="#prozess" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text }}>Prozess</a>
                <Link to="/website-wirkung" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                <Link to="/ueber-uns" className="font-medium text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                <Link to="/termin-buchen">
                  <button className="px-5 py-2.5 font-medium text-sm rounded-sm transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer" style={{ backgroundColor: theme.colors.accent, color: '#FFFFFF' }}>Termin buchen</button>
                </Link>
              </div>
              <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: theme.colors.text }}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-8">
              <div className="w-28 h-px" style={{ backgroundColor: theme.colors.accent }}></div>
              <div className="mx-6"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: theme.colors.accent }}></div></div>
              <div className="w-28 h-px" style={{ backgroundColor: theme.colors.accent }}></div>
            </div>
            <span className="text-xs tracking-[0.4em] uppercase font-semibold" style={{ color: theme.colors.accent }}>Exzellenz in Webdesign</span>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight tracking-tight mt-4" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>{tonality.hero.headline}</h1>
            <p className="text-base mb-8 max-w-3xl mx-auto leading-relaxed" style={{ color: theme.colors.textSecondary }}>{tonality.hero.subheadline}</p>
            <div className="flex gap-4 justify-center flex-wrap mb-10">
              <a href="tel:+491607676763" className="flex items-center gap-2 px-8 py-3 font-medium text-sm tracking-wide transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: theme.colors.accent }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <i className="ri-phone-line text-sm"></i>
                Jetzt anrufen
              </a>
              <Link to="/termin-buchen" className="flex items-center gap-2 px-8 py-3 font-medium text-sm tracking-wide transition-all duration-500 hover:shadow-xl hover:-translate-y-1 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: 'transparent', border: `3px solid ${theme.colors.accent}`, backdropFilter: 'blur(8px)' }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                Online-Termin vereinbaren
              </Link>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{__html: `window.addEventListener('scroll',function(){var n=document.getElementById('navbar-premium');if(n){if(window.scrollY>50){n.style.backgroundColor='rgba(255,255,255,0.98)';n.style.boxShadow='0 2px 20px rgba(0,0,0,0.1)';}else{n.style.backgroundColor='rgba(255,255,255,0.95)';n.style.boxShadow='0 1px 10px rgba(0,0,0,0.05)';}}})`}} />
      </section>
    );
  }

  // Playful Look
  if (currentLook === 'playful') {
    return (
      <section className="min-h-screen relative overflow-hidden" style={{ fontFamily: theme.fonts.body, background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.cardBg} 50%, ${theme.colors.background} 100%)` }}>
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md" id="navbar-playful" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setCurrentLook('seitenschmied'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Zurück zur Startseite">
                  <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png" alt="seitenschmied" className="h-7 w-auto" />
                </button>
              </div>
              <div className="hidden md:flex items-center gap-7">
                <a href="#prozess" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text }}>Prozess</a>
                <Link to="/website-wirkung" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Gute Gründe</Link>
                <Link to="/ueber-uns" className="font-bold text-sm hover:opacity-70 transition-opacity whitespace-nowrap" style={{ color: theme.colors.text }}>Über Seitenschmied</Link>
                <Link to="/termin-buchen">
                  <button className="px-5 py-2.5 font-black text-sm rounded-full transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer" style={{ background: `linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.secondary} 100%)`, color: '#FFFFFF' }}>Termin buchen</button>
                </Link>
              </div>
              <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: theme.colors.text }}>
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="absolute inset-0 z-0 opacity-15">
          <img src="https://readdy.ai/api/search-image?query=Vibrant%20abstract%20gradient%20background%20with%20smooth%20flowing%20organic%20shapes%20in%20bright%20purple%20pink%20orange%20yellow%20green%20colors%2C%20modern%20playful%20design%20with%20soft%20rounded%20blobs%20and%20curves%2C%20energetic%20joyful%20atmosphere%20with%20dynamic%20movement%2C%20contemporary%20digital%20art%20style%20with%20depth%20and%20layers%2C%20cheerful%20bubbly%20aesthetic%20with%20high%20saturation%2C%20minimalist%20geometric%20elements%20floating%2C%20soft%20lighting%20and%20highlights%20creating%20dimension%2C%20fun%20creative%20composition%20with%20overlapping%20translucent%20shapes%2C%20bright%20color%20palette%20with%20smooth%20transitions&width=1920&height=1080&seq=playful-hero-bg-v2-001&orientation=landscape" alt="Bunte Grafiken" className="w-full h-full object-cover" style={{ objectPosition: 'center' }} />
        </div>
        <div className="absolute top-32 right-20 w-32 h-32 rounded-full opacity-40 animate-bounce" style={{ background: `radial-gradient(circle, ${theme.colors.accent} 0%, ${theme.colors.secondary} 100%)`, animationDuration: '4s', filter: 'blur(20px)' }}></div>
        <div className="absolute bottom-32 left-16 w-28 h-28 rounded-full opacity-35 animate-pulse" style={{ background: `radial-gradient(circle, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`, animationDuration: '3s', filter: 'blur(20px)' }}></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 pt-32 pb-16">
          <div className="text-center mb-12">
            <div className="mb-8 animate-bounce" style={{ animationDuration: '2s' }}>
              <img src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/6a1f0edae4fce559ce3adc7194b5ce70.png" alt="seitenschmied.com" className="h-16 w-auto mx-auto" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-black mb-6 leading-tight" style={{ fontFamily: theme.fonts.heading, background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 50%, ${theme.colors.accent} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{tonality.hero.headline}</h1>
            <p className="text-lg mb-6 font-bold leading-relaxed" style={{ color: theme.colors.textSecondary }}>{tonality.hero.subheadline}</p>
            <div className="flex gap-4 justify-center flex-wrap mb-12">
              <a href="tel:+491607676763" className="flex items-center gap-2 px-8 py-4 font-black text-base transition-all duration-300 hover:scale-110 hover:rotate-2 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: theme.colors.accent }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <i className="ri-phone-line text-base"></i>
                Jetzt anrufen 📞
              </a>
              <Link to="/termin-buchen" className="flex items-center gap-2 px-8 py-4 font-black text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer" style={{ color: theme.colors.text, backgroundColor: 'transparent', border: `3px solid ${theme.colors.accent}`, backdropFilter: 'blur(8px)' }} onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                Online-Termin vereinbaren
              </Link>
            </div>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{__html: `window.addEventListener('scroll',function(){var n=document.getElementById('navbar-playful');if(n){if(window.scrollY>50){n.style.backgroundColor='rgba(255,255,255,0.98)';n.style.boxShadow='0 2px 20px rgba(0,0,0,0.1)';}else{n.style.backgroundColor='rgba(255,255,255,0.95)';n.style.boxShadow='0 1px 10px rgba(0,0,0,0.05)';}}})`}} />
      </section>
    );
  }

  return null;
}
